import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, CalendarDays,
  MapPin, Search, Filter, UserCheck
} from "lucide-react";
import { format, addDays, startOfWeek, startOfMonth, endOfMonth, isSameDay, isSameMonth, addMonths } from "date-fns";
import { es } from "date-fns/locale";
import DetailEditForm from "@/components/DetailEditForm";
import { generateSlots } from "@/utils/slots";
import usePersistedState from "@/hooks/usePersistedState";

// ═══ STATUS CONFIG ═══
const STATUS_CFG = {
  pendiente_confirmacion: { label: "Pte. Confirm.", color: "#f59e0b", bg: "bg-amber-50", text: "text-amber-800", border: "border-l-amber-400" },
  confirmada: { label: "Confirmada", color: "#3b82f6", bg: "bg-blue-50", text: "text-blue-800", border: "border-l-blue-500" },
  pendiente_reprogramacion: { label: "Pte. Reprog.", color: "#8b5cf6", bg: "bg-purple-50", text: "text-purple-800", border: "border-l-purple-500" },
  cancelada_cliente: { label: "Cancel. Cliente", color: "#ef4444", bg: "bg-red-50", text: "text-red-700", border: "border-l-red-400" },
  cancelada_taller: { label: "Cancel. Taller", color: "#ef4444", bg: "bg-red-50", text: "text-red-700", border: "border-l-red-400" },
  pendiente: { label: "Pendiente", color: "#f59e0b", bg: "bg-amber-50", text: "text-amber-800", border: "border-l-amber-400" },
};

const CENTER_COLOR_PALETTE = [
  { bg: "bg-sky-50", border: "border-sky-300", badge: "bg-sky-100 text-sky-700 border-sky-200", dot: "bg-sky-500" },
  { bg: "bg-emerald-50", border: "border-emerald-300", badge: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  { bg: "bg-violet-50", border: "border-violet-300", badge: "bg-violet-100 text-violet-700 border-violet-200", dot: "bg-violet-500" },
  { bg: "bg-amber-50", border: "border-amber-300", badge: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  { bg: "bg-rose-50", border: "border-rose-300", badge: "bg-rose-100 text-rose-700 border-rose-200", dot: "bg-rose-500" },
];
const DEFAULT_CENTER_COLOR = CENTER_COLOR_PALETTE[0];

const ADMIN_STATUSES = [
  { value: "pendiente_confirmacion", label: "Pte. Confirmacion" },
  { value: "confirmada", label: "Confirmada" },
  { value: "pendiente_reprogramacion", label: "Pte. Reprogramacion" },
  { value: "cancelada_cliente", label: "Cancelada (cliente)" },
  { value: "cancelada_taller", label: "Cancelada (taller)" },
];

const DAY_MAP = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];

export default function CalendarView({ appointments, onRefresh, config, advisors }) {
  const { axiosAuth } = useAuth();
  const [view, setView] = usePersistedState("appointments.calendar.view", "week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [detailApt, setDetailApt] = useState(null);
  const [overflowSlot, setOverflowSlot] = useState(null); // { dateStr, slot, apts }
  const [moveConfirm, setMoveConfirm] = useState(null);
  const [dragData, setDragData] = useState(null);
  const [centersList, setCentersList] = useState([]);

  // Filters (Iter 148: persistidos en localStorage)
  const [selectedStatuses, setSelectedStatuses] = usePersistedState("appointments.calendar.statuses", []);
  const [centerFilter, setCenterFilter] = usePersistedState("appointments.calendar.center", "todos");
  const [advisorFilter, setAdvisorFilter] = usePersistedState("appointments.calendar.advisor", "todos");
  const [searchQuery, setSearchQuery] = usePersistedState("appointments.calendar.search", "");
  // Iter 148: por defecto OCULTAS las canceladas en el calendario.
  // El toggle permite mostrarlas con estilo discreto si el operador las necesita.
  const [showCancelled, setShowCancelled] = usePersistedState("appointments.calendar.showCancelled", false);

  // Fetch centers from DB
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/centers`).then(r => r.json())
      .then(d => setCentersList(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // Build dynamic center colors map from fetched centers
  const centerColors = useMemo(() => {
    const map = {};
    centersList.forEach((c, i) => { map[c.slug] = CENTER_COLOR_PALETTE[i % CENTER_COLOR_PALETTE.length]; });
    return map;
  }, [centersList]);

  const interval = config?.appointment_interval || 20;
  const workingHours = config?.working_hours || {};
  const activeAdvisors = (advisors || []).filter(a => a.active);
  const capacityPerSlot = Math.max(activeAdvisors.length, 1);

  // ═══ DATES ═══
  const viewDates = useMemo(() => {
    if (view === "day") return [currentDate];
    if (view === "month") return []; // month view doesn't use viewDates grid
    return Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), i));
  }, [view, currentDate]);

  // Month calendar dates
  const monthDates = useMemo(() => {
    if (view !== "month") return [];
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const startWeek = startOfWeek(start, { weekStartsOn: 1 });
    const days = [];
    let d = startWeek;
    while (d <= end || days.length % 7 !== 0) {
      days.push(new Date(d));
      d = addDays(d, 1);
    }
    return days;
  }, [view, currentDate]);

  const isDayClosed = (d) => {
    const h = workingHours[DAY_MAP[d.getDay()]];
    return !h || h.toLowerCase() === "cerrado";
  };

  const allSlots = useMemo(() => {
    const set = new Set();
    viewDates.forEach(d => {
      const h = workingHours[DAY_MAP[d.getDay()]];
      generateSlots(h, interval).forEach(s => set.add(s));
    });
    return Array.from(set).sort();
  }, [viewDates, workingHours, interval]);

  // ═══ FILTER ═══
  // Iter 148: helper unificado para detectar canceladas (cubre todos los sinónimos).
  const isCancelled = (a) => {
    const s = (a?.status || "").toLowerCase();
    return s === "cancelada" || s.startsWith("cancelada_") || s.includes("cancel");
  };

  const filteredApts = useMemo(() => {
    return appointments.filter(a => {
      // Iter 148: OCULTAR canceladas por defecto. Si el usuario selecciona
      // explícitamente un status "cancelada_*" en el filtro, lo respetamos
      // (es una intención clara de verlas). Si no, depende del toggle.
      const explicitlyAskingForCancelled = selectedStatuses.some(s => s.includes("cancelada"));
      if (!showCancelled && !explicitlyAskingForCancelled && isCancelled(a)) return false;

      if (selectedStatuses.length > 0 && !selectedStatuses.includes(a.status)) return false;
      if (centerFilter !== "todos" && a.center !== centerFilter) return false;
      if (advisorFilter === "sin_asignar" && a.advisor_id) return false;
      if (advisorFilter !== "todos" && advisorFilter !== "sin_asignar" && a.advisor_id !== advisorFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(a.client_name || "").toLowerCase().includes(q) &&
            !(a.plate || "").toLowerCase().includes(q) &&
            !(a.service_type || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [appointments, selectedStatuses, centerFilter, advisorFilter, searchQuery, showCancelled]);

  const aptsForDate = useCallback((d) => {
    const ds = format(d, "yyyy-MM-dd");
    return filteredApts.filter(a => a.date === ds);
  }, [filteredApts]);

  // ═══ NAV ═══
  const goToday = () => setCurrentDate(new Date());
  const goPrev = () => setCurrentDate(p => view === "day" ? addDays(p, -1) : view === "month" ? addMonths(p, -1) : addDays(p, -7));
  const goNext = () => setCurrentDate(p => view === "day" ? addDays(p, 1) : view === "month" ? addMonths(p, 1) : addDays(p, 7));
  const viewTitle = view === "day"
    ? format(currentDate, "EEEE d 'de' MMMM yyyy", { locale: es })
    : view === "month"
    ? format(currentDate, "MMMM yyyy", { locale: es })
    : viewDates.length >= 7 ? `${format(viewDates[0], "d MMM", { locale: es })} - ${format(viewDates[6], "d MMM yyyy", { locale: es })}` : "";

  // ═══ STATS ═══
  const viewAptsForStats = useMemo(() => {
    if (view === "month") {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      const sd = format(start, "yyyy-MM-dd");
      const ed = format(end, "yyyy-MM-dd");
      return filteredApts.filter(a => a.date >= sd && a.date <= ed);
    }
    return viewDates.flatMap(d => aptsForDate(d));
  }, [view, currentDate, filteredApts, viewDates, aptsForDate]);

  const stats = {
    total: viewAptsForStats.length,
    pendientes: viewAptsForStats.filter(a => a.status === "pendiente_confirmacion" || a.status === "pendiente").length,
    confirmadas: viewAptsForStats.filter(a => a.status === "confirmada").length,
    reprogramacion: viewAptsForStats.filter(a => a.status === "pendiente_reprogramacion").length,
    canceladas: viewAptsForStats.filter(a => a.status?.includes("cancelada")).length,
  };

  // ═══ STATUS TOGGLE ═══
  const toggleStatus = (s) => {
    setSelectedStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  // ═══ DRAG & DROP ═══
  const handleDragStart = (e, apt) => {
    setDragData({ id: apt.id, originalDate: apt.date, originalSlot: apt.time_slot });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", apt.id);
  };
  const handleDrop = (e, dateStr, slot) => {
    e.preventDefault();
    if (!dragData || (dragData.originalDate === dateStr && dragData.originalSlot === slot)) { setDragData(null); return; }
    const apt = appointments.find(a => a.id === dragData.id);
    if (apt) setMoveConfirm({ apt, newDate: dateStr, newSlot: slot });
    setDragData(null);
  };
  const confirmMove = async () => {
    if (!moveConfirm) return;
    try {
      await axiosAuth().put(`/appointments/${moveConfirm.apt.id}`, { date: moveConfirm.newDate, time_slot: moveConfirm.newSlot });
      toast.success("Cita reprogramada");
      onRefresh();
    } catch { toast.error("Error al mover cita"); }
    setMoveConfirm(null);
  };

  // ═══ ACTIONS ═══

  // ═══ CAPACITY PER CELL ═══
  const getCellCapacity = (dateStr, slot) => {
    const nonCancelled = appointments.filter(a =>
      a.date === dateStr && a.time_slot === slot && !a.status?.includes("cancelada")
    );
    return { used: nonCancelled.length, capacity: capacityPerSlot };
  };

  // ═══ DAY LOAD INDICATOR ═══
  const thresholds = config?.load_thresholds || { green: 60, yellow: 85 };
  const getDayLoad = useCallback((d) => {
    const ds = format(d, "yyyy-MM-dd");
    const daySlots = generateSlots(workingHours[DAY_MAP[d.getDay()]], interval);
    if (!daySlots.length) return null;
    const totalCap = capacityPerSlot * daySlots.length;
    const used = appointments.filter(a =>
      a.date === ds && !a.status?.includes("cancelada")
    ).length;
    const pct = totalCap > 0 ? Math.round(used / totalCap * 100) : 0;
    const level = pct < thresholds.green ? "low" : pct < thresholds.yellow ? "medium" : "high";
    return { pct, level, used, totalCap };
  }, [appointments, workingHours, interval, capacityPerSlot, thresholds]);

  const MAX_VISIBLE = 5;

  return (
    <div className="space-y-3" data-testid="calendar-view">
      {/* ═══ HEADER ═══ */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goPrev} className="rounded-sm h-8 w-8 p-0" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={goToday} className="rounded-sm h-8 text-xs uppercase tracking-wider font-semibold px-3" data-testid="cal-today">Hoy</Button>
          <Button variant="outline" size="sm" onClick={goNext} className="rounded-sm h-8 w-8 p-0" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <h2 className="text-sm font-semibold text-slate-900 capitalize ml-2" data-testid="cal-title">{viewTitle}</h2>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input placeholder="Buscar..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 h-8 w-36 rounded-sm text-xs border-slate-200" data-testid="cal-search" />
          </div>
          <Select value={centerFilter} onValueChange={setCenterFilter}>
            <SelectTrigger className="h-8 w-32 rounded-sm text-xs border-slate-200" data-testid="cal-filter-center"><MapPin className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos centros</SelectItem>
              {centersList.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={advisorFilter} onValueChange={setAdvisorFilter}>
            <SelectTrigger className="h-8 w-36 rounded-sm text-xs border-slate-200" data-testid="cal-filter-advisor"><UserCheck className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos asesores</SelectItem>
              <SelectItem value="sin_asignar">Sin asignar</SelectItem>
              {activeAdvisors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Iter 148 — Toggle "Mostrar canceladas" (apagado por defecto) */}
          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-600 select-none border border-slate-200 rounded-sm h-8 px-2.5 hover:bg-slate-50" data-testid="cal-toggle-cancelled-label">
            <Checkbox
              checked={showCancelled}
              onCheckedChange={(v) => setShowCancelled(!!v)}
              data-testid="cal-toggle-cancelled"
            />
            <span className="uppercase tracking-wider font-semibold">Mostrar canceladas</span>
          </label>
          {/* Multi-status filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs border-slate-200 px-2.5" data-testid="cal-filter-status">
                <Filter className="w-3 h-3 mr-1" /> Estado {selectedStatuses.length > 0 && <Badge className="ml-1 h-4 w-4 p-0 text-[9px] rounded-full bg-red-600 text-white border-0 flex items-center justify-center">{selectedStatuses.length}</Badge>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-3" align="end">
              <div className="space-y-2">
                <button onClick={() => setSelectedStatuses([])} className="text-xs text-blue-600 hover:underline font-medium" data-testid="status-filter-all">Mostrar todos</button>
                {ADMIN_STATUSES.map(s => (
                  <label key={s.value} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={selectedStatuses.includes(s.value)} onCheckedChange={() => toggleStatus(s.value)} />
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_CFG[s.value]?.color }} />
                    <span className="text-xs text-slate-700">{s.label}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <div className="flex border border-slate-200 rounded-sm overflow-hidden">
            <button onClick={() => setView("day")} className={`px-3 h-8 text-xs font-semibold ${view === "day" ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="cal-view-day">Dia</button>
            <button onClick={() => setView("week")} className={`px-3 h-8 text-xs font-semibold ${view === "week" ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="cal-view-week">Semana</button>
            <button onClick={() => setView("month")} className={`px-3 h-8 text-xs font-semibold ${view === "month" ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="cal-view-month">Mes</button>
          </div>
        </div>
      </div>

      {/* ═══ SUMMARY BAR ═══ */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="cal-summary">
        {[
          { n: stats.total, label: "Total", cls: "bg-white border-slate-200", tcls: "text-slate-900", lcls: "text-slate-400" },
          { n: stats.pendientes, label: "Pendientes", cls: "bg-amber-50 border-amber-200", tcls: "text-amber-700", lcls: "text-amber-500" },
          { n: stats.confirmadas, label: "Confirmadas", cls: "bg-blue-50 border-blue-200", tcls: "text-blue-700", lcls: "text-blue-500" },
          { n: stats.reprogramacion, label: "Reprog.", cls: "bg-purple-50 border-purple-200", tcls: "text-purple-700", lcls: "text-purple-500" },
          { n: stats.canceladas, label: "Canceladas", cls: "bg-red-50 border-red-200", tcls: "text-red-600", lcls: "text-red-400" },
        ].map(s => (
          <div key={s.label} className={`${s.cls} border rounded-sm p-2 text-center`}>
            <p className={`text-lg font-bold ${s.tcls}`}>{s.n}</p>
            <p className={`text-[10px] uppercase tracking-wider ${s.lcls} font-semibold`}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Center legend */}
      <div className="flex items-center gap-4 text-xs text-slate-500">
        {centersList.map((c, i) => {
          const cc = CENTER_COLOR_PALETTE[i % CENTER_COLOR_PALETTE.length];
          return <span key={c.slug} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${cc.dot}`} /> {c.name}</span>;
        })}
        <span className="ml-auto font-mono text-slate-400">Capacidad: {capacityPerSlot} citas/franja ({interval} min)</span>
      </div>

      {/* ═══ MONTH VIEW ═══ */}
      {view === "month" && (
        <Card className="border-slate-200 rounded-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="grid grid-cols-7">
              {["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"].map(d => (
                <div key={d} className="p-2 text-center text-[10px] uppercase tracking-wider font-semibold text-slate-400 bg-slate-50 border-b border-r border-slate-200 last:border-r-0">{d}</div>
              ))}
              {monthDates.map((d, i) => {
                const ds = format(d, "yyyy-MM-dd");
                const isToday = isSameDay(d, new Date());
                const inMonth = isSameMonth(d, currentDate);
                const closed = isDayClosed(d);
                const dayApts = filteredApts.filter(a => a.date === ds);
                const load = !closed ? getDayLoad(d) : null;
                const loadColor = load ? (load.level === "low" ? "bg-green-500" : load.level === "medium" ? "bg-amber-500" : "bg-red-500") : "";
                return (
                  <div key={i}
                    className={`min-h-[90px] border-b border-r border-slate-100 last:border-r-0 p-1.5 ${!inMonth ? 'bg-slate-50/50' : ''} ${closed ? 'bg-slate-50' : ''} ${isToday ? 'ring-1 ring-inset ring-red-300' : ''}`}
                    data-testid={`month-cell-${ds}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-bold ${isToday ? 'text-red-600' : inMonth ? 'text-slate-900' : 'text-slate-300'}`}>{format(d, "d")}</span>
                      {dayApts.length > 0 && (
                        <span className="text-[9px] font-bold bg-slate-900 text-white rounded-full w-5 h-5 flex items-center justify-center">{dayApts.length}</span>
                      )}
                    </div>
                    {load && !closed && (
                      <div className="mb-1">
                        <div className="w-full bg-slate-200 rounded-full h-1 overflow-hidden">
                          <div className={`h-full rounded-full ${loadColor}`} style={{ width: `${Math.min(load.pct, 100)}%` }} />
                        </div>
                        <span className={`text-[7px] font-semibold ${load.level === "low" ? 'text-green-600' : load.level === "medium" ? 'text-amber-600' : 'text-red-600'}`}>
                          {load.pct}%
                        </span>
                      </div>
                    )}
                    {closed && inMonth && <span className="text-[8px] text-slate-400">Cerrado</span>}
                    <div className="space-y-px">
                      {dayApts.slice(0, 3).map(apt => {
                        const st = STATUS_CFG[apt.status] || STATUS_CFG.pendiente;
                        return (
                          <div key={apt.id} onClick={() => setDetailApt(apt)}
                            className={`text-[8px] px-1 py-px rounded-sm truncate cursor-pointer ${st.bg} ${st.text} border-l-2 ${st.border} hover:shadow-sm`}
                            data-testid={`month-apt-${apt.id}`}>
                            <span className="font-semibold">{apt.time_slot}</span> {apt.client_name}
                          </div>
                        );
                      })}
                      {dayApts.length > 3 && (
                        <button onClick={() => { setView("day"); setCurrentDate(d); }}
                          className="text-[8px] text-blue-600 font-semibold hover:underline w-full text-left pl-1"
                          data-testid={`month-more-${ds}`}>
                          +{dayApts.length - 3} mas
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══ DAY/WEEK GRID ═══ */}
      {(view === "day" || view === "week") && (
      <Card className="border-slate-200 rounded-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Day headers */}
              <div className="grid border-b border-slate-200 bg-slate-50 sticky top-0 z-10"
                style={{ gridTemplateColumns: `60px repeat(${viewDates.length}, 1fr)` }}>
                <div className="p-2 border-r border-slate-200" />
                {viewDates.map(d => {
                  const isToday = isSameDay(d, new Date());
                  const closed = isDayClosed(d);
                  const load = !closed ? getDayLoad(d) : null;
                  const loadColor = load ? (load.level === "low" ? "bg-green-500" : load.level === "medium" ? "bg-amber-500" : "bg-red-500") : "";
                  const loadLabel = load ? (load.level === "low" ? "Baja" : load.level === "medium" ? "Media" : "Alta") : "";
                  return (
                    <div key={d.toISOString()} className={`p-1.5 text-center border-r border-slate-200 last:border-r-0 ${closed ? 'bg-slate-100' : ''}`}>
                      <p className={`text-[10px] uppercase tracking-wider font-semibold ${isToday ? 'text-red-600' : 'text-slate-400'}`}>{format(d, "EEE", { locale: es })}</p>
                      <p className={`text-sm font-bold ${isToday ? 'text-red-600' : 'text-slate-900'}`}>{format(d, "d")}</p>
                      {closed && <span className="text-[9px] text-slate-400 font-normal">Cerrado</span>}
                      {load && (
                        <div className="mt-0.5" data-testid={`load-${format(d, "yyyy-MM-dd")}`}>
                          <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${loadColor}`} style={{ width: `${Math.min(load.pct, 100)}%` }} />
                          </div>
                          <span className={`text-[8px] font-semibold ${load.level === "low" ? 'text-green-600' : load.level === "medium" ? 'text-amber-600' : 'text-red-600'}`}>
                            {loadLabel} {load.pct}%
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Rows */}
              {allSlots.map(slot => (
                <div key={slot} className="grid border-b border-slate-100"
                  style={{ gridTemplateColumns: `60px repeat(${viewDates.length}, 1fr)` }}>
                  <div className="p-1 pr-2 text-right border-r border-slate-200 flex items-start justify-end">
                    <span className="text-[10px] font-mono text-slate-400 mt-0.5">{slot}</span>
                  </div>
                  {viewDates.map(d => {
                    const dateStr = format(d, "yyyy-MM-dd");
                    const closed = isDayClosed(d);
                    const cellApts = aptsForDate(d).filter(a => a.time_slot === slot);
                    const { used, capacity } = getCellCapacity(dateStr, slot);
                    const satRatio = used / capacity;
                    const isFull = used >= capacity;
                    const satClass = satRatio >= 1 ? 'bg-red-50/70' : satRatio >= 0.66 ? 'bg-amber-50/50' : '';

                    return (
                      <div key={`${dateStr}-${slot}`}
                        className={`border-r border-slate-100 last:border-r-0 relative p-px ${closed ? 'bg-slate-50/50' : satClass}`}
                        onDragOver={e => { if (!closed) { e.preventDefault(); e.currentTarget.style.outline = '2px solid #3b82f6'; } }}
                        onDragLeave={e => { e.currentTarget.style.outline = ''; }}
                        onDrop={e => { e.currentTarget.style.outline = ''; if (!closed) handleDrop(e, dateStr, slot); }}
                        data-testid={`cell-${dateStr}-${slot}`}
                      >
                        {/* Capacity indicator */}
                        {!closed && (
                          <div className={`absolute top-0 right-0.5 text-[7px] font-mono font-bold px-0.5 rounded-sm z-10 ${isFull ? 'bg-red-200 text-red-700' : used > 0 ? 'bg-slate-200 text-slate-500' : 'text-slate-300'}`} data-testid={`cap-${dateStr}-${slot}`}>
                            {used}/{capacity}
                          </div>
                        )}

                        {/* Compact appointment blocks */}
                        {cellApts.slice(0, MAX_VISIBLE).map(apt => {
                          const st = STATUS_CFG[apt.status] || STATUS_CFG.pendiente;
                          const cc = centerColors[apt.center] || DEFAULT_CENTER_COLOR;
                          const isCancelled = apt.status?.includes("cancelada");
                          return (
                            <div key={apt.id}
                              draggable={!isCancelled}
                              onDragStart={e => handleDragStart(e, apt)}
                              onClick={() => setDetailApt(apt)}
                              className={`px-1 py-px rounded-sm border-l-2 ${st.border} ${cc.bg} cursor-pointer hover:shadow-sm transition-shadow ${isCancelled ? 'opacity-40' : ''} mb-px`}
                              data-testid={`cal-apt-${apt.id}`}
                              title={`${apt.client_name} | ${apt.plate || ''} | ${apt.service_type || ''} | ${apt.advisor_name || 'Sin asignar'}`}
                            >
                              <div className="flex items-center gap-0.5 min-w-0">
                                <span className={`w-1 h-1 rounded-full flex-shrink-0 ${cc.dot}`} />
                                <span className={`text-[10px] font-semibold ${st.text} truncate leading-tight`}>{apt.client_name}</span>
                              </div>
                              <div className="flex items-center gap-1 min-w-0">
                                {apt.plate && <span className="text-[8px] font-mono text-slate-500 truncate leading-tight">{apt.plate}</span>}
                                <span className="text-[8px] text-slate-400 truncate leading-tight">{apt.service_type}</span>
                              </div>
                              <div className="flex items-center gap-1 min-w-0">
                                {!apt.advisor_id ? (
                                  <span className="text-[7px] font-bold text-orange-600 bg-orange-50 px-0.5 rounded-sm leading-tight">SIN ASIGNAR</span>
                                ) : (
                                  <span className="text-[8px] text-slate-400 truncate leading-tight">{apt.advisor_name}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {/* Overflow indicator */}
                        {cellApts.length > MAX_VISIBLE && (
                          <button
                            onClick={() => setOverflowSlot({ dateStr, slot, apts: cellApts })}
                            className="w-full text-center text-[9px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-sm py-px transition-colors"
                            data-testid={`overflow-${dateStr}-${slot}`}
                          >
                            +{cellApts.length - MAX_VISIBLE} mas
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* ═══ OVERFLOW DIALOG - FULL OPERATIONAL PANEL ═══ */}
      <Dialog open={!!overflowSlot} onOpenChange={() => setOverflowSlot(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[80vh] overflow-y-auto" data-testid="overflow-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-base uppercase flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-red-600" />
              {overflowSlot?.apts?.length} citas &mdash; {overflowSlot?.slot} &middot; {overflowSlot?.dateStr}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-1">
            {overflowSlot?.apts.map(apt => {
              const st = STATUS_CFG[apt.status] || STATUS_CFG.pendiente;
              const cc = centerColors[apt.center] || DEFAULT_CENTER_COLOR;
              const isCancelled = apt.status?.includes("cancelada");
              return (
                <div key={apt.id}
                  className={`p-2.5 rounded-sm border-l-[3px] ${st.border} ${cc.bg} ${isCancelled ? 'opacity-50' : ''}`}
                  data-testid={`overflow-apt-${apt.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cc.dot}`} />
                        <p className={`text-sm font-semibold ${st.text} truncate`}>{apt.client_name}</p>
                        <Badge className={`rounded-sm text-[8px] ${st.bg} ${st.text} border-0 shrink-0`}>{st.label}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-slate-600 ml-4">
                        <span><strong className="text-slate-400">Matricula:</strong> <span className="font-mono">{apt.plate || '—'}</span></span>
                        <span><strong className="text-slate-400">Vehiculo:</strong> {apt.brand} {apt.model_name}</span>
                        <span><strong className="text-slate-400">Servicio:</strong> {apt.service_type}</span>
                        <span><strong className="text-slate-400">Centro:</strong> {apt.center_name}</span>
                        <span><strong className="text-slate-400">Asesor:</strong> {apt.advisor_name || <span className="text-orange-600 font-semibold">SIN ASIGNAR</span>}</span>
                        <span><strong className="text-slate-400">Estado:</strong> <span className={st.text}>{st.label}</span></span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline"
                        onClick={() => { setOverflowSlot(null); setDetailApt(apt); }}
                        className="h-6 text-[10px] rounded-sm px-2 uppercase tracking-wider font-semibold"
                        data-testid={`overflow-edit-${apt.id}`}>
                        Editar
                      </Button>
                      {!isCancelled && apt.status === "pendiente_confirmacion" && (
                        <Button size="sm"
                          onClick={async () => {
                            try { await axiosAuth().put(`/appointments/${apt.id}`, { status: "confirmada" }); toast.success("Confirmada"); onRefresh(); setOverflowSlot(null); } catch { toast.error("Error"); }
                          }}
                          className="h-6 text-[10px] rounded-sm px-2 bg-blue-600 hover:bg-blue-700 text-white uppercase tracking-wider font-semibold"
                          data-testid={`overflow-confirm-${apt.id}`}>
                          Confirmar
                        </Button>
                      )}
                      {!isCancelled && (
                        <Button size="sm" variant="outline"
                          onClick={async () => {
                            try { await axiosAuth().put(`/appointments/${apt.id}`, { status: "cancelada_taller" }); toast.success("Cancelada"); onRefresh(); setOverflowSlot(null); } catch { toast.error("Error"); }
                          }}
                          className="h-6 text-[10px] rounded-sm px-2 text-red-600 border-red-200 hover:bg-red-50 uppercase tracking-wider font-semibold"
                          data-testid={`overflow-cancel-${apt.id}`}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ DETAIL DIALOG - FULL EDIT ═══ */}
      <Dialog open={!!detailApt} onOpenChange={() => setDetailApt(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="cal-detail-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-red-600" /> Detalle de Cita
            </DialogTitle>
          </DialogHeader>
          {detailApt && <DetailEditForm apt={detailApt} config={config} onClose={() => setDetailApt(null)} onSave={async (updated) => {
            try {
              await axiosAuth().put(`/appointments/${detailApt.id}`, updated);
              toast.success("Cita actualizada. Se notificara al cliente si aplica.");
              setDetailApt(null);
              onRefresh();
            } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
          }} axiosAuth={axiosAuth} />}
        </DialogContent>
      </Dialog>

      {/* ═══ MOVE CONFIRM ═══ */}
      <Dialog open={!!moveConfirm} onOpenChange={() => setMoveConfirm(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="move-confirm-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-base uppercase">Reprogramar Cita</DialogTitle></DialogHeader>
          {moveConfirm && (
            <div className="py-2 space-y-3">
              <p className="text-sm text-slate-700">Mover la cita de <strong>{moveConfirm.apt.client_name}</strong>:</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-sm p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Actual</p>
                  <p className="text-sm font-mono font-bold text-slate-900 mt-1">{(() => { const p = moveConfirm.apt.date?.split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : moveConfirm.apt.date; })()}</p>
                  <p className="text-xs font-mono text-slate-600">{moveConfirm.apt.time_slot}</p>
                </div>
                <div className="bg-blue-50 rounded-sm p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-blue-500 font-semibold">Nueva</p>
                  <p className="text-sm font-mono font-bold text-blue-900 mt-1">{(() => { const p = moveConfirm.newDate?.split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : moveConfirm.newDate; })()}</p>
                  <p className="text-xs font-mono text-blue-700">{moveConfirm.newSlot}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveConfirm(null)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={confirmMove} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="move-confirm">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
